import { useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { mockPlStructure, PL_ANCHORS } from "../constants";
import { calculateOomFromPlValues } from "utils/utils";

export function usePnlConfig(options = {}) {
  const {
    translateStructure = false,
    initialDrillDowns = ["logistics"],
    pnlMetadata = null,
    plValues = null,
  } = options;

  const { t } = useTranslation();

  const plStructureValue = useMemo(() => {
    const sourceStructure = pnlMetadata?.pl?.pl_structure || mockPlStructure;
    return sourceStructure.map((line, index) => {
      const translatedName = translateStructure
        ? t(`lines.${line.meta_name}`, { defaultValue: line.name })
        : line.name;
      return {
        ...line,
        index,
        name: translatedName,
        hasChildren: line.is_parent,
      };
    });
  }, [pnlMetadata, translateStructure, t]);

  const plStructure = useRef(plStructureValue);
  const netRevenueAnchor = useRef(
    pnlMetadata?.pl?.net_revenue_anchor || PL_ANCHORS.netRevenue,
  );
  const serveMarginAnchor = useRef(
    pnlMetadata?.pl?.serve_margin_anchor || PL_ANCHORS.serveMargin,
  );
  const volumeUnit = useRef(
    pnlMetadata?.pl?.volume_unit || PL_ANCHORS.volumeUnit,
  );
  const volumeL12M = useRef(null);

  plStructure.current = plStructureValue;
  netRevenueAnchor.current =
    pnlMetadata?.pl?.net_revenue_anchor || PL_ANCHORS.netRevenue;
  serveMarginAnchor.current =
    pnlMetadata?.pl?.serve_margin_anchor || PL_ANCHORS.serveMargin;
  volumeUnit.current = pnlMetadata?.pl?.volume_unit || PL_ANCHORS.volumeUnit;

  // Order of magnitude - calculated from actual P&L values when available
  const oom = useMemo(() => {
    if (plValues && Object.keys(plValues).length > 0) {
      return calculateOomFromPlValues(plValues, t);
    }
    return { div: 1e6, x: " " + t("oom.m") };
  }, [plValues, t]);

  const [drillDownsOpen, setDrillDownsOpen] = useState(initialDrillDowns);
  const [mouseOverPLLine, setMouseOverPLLine] = useState(null);
  const [mouseOverPLLineFade, setMouseOverPLLineFade] = useState(null);

  const handleMouseOverPLLine = useCallback((meta_name) => {
    setMouseOverPLLine(meta_name);
    setMouseOverPLLineFade(meta_name);
  }, []);

  const handleMouseLeavePLLine = useCallback(() => {
    setMouseOverPLLine(null);
    setMouseOverPLLineFade(null);
  }, []);

  const staticPnlProps = useMemo(
    () => ({
      revenueAnchors: PL_ANCHORS.revenueAnchors,
      marginAnchors: PL_ANCHORS.marginAnchors,
      currencyUnit: PL_ANCHORS.currencyUnit,
      isLoading: false,
      isRefreshing: false,
      plsToRefresh: [],
      plsOnError: [],
    }),
    [],
  );

  const timersAllowedRef = useRef(true);

  return {
    plStructure,
    netRevenueAnchor,
    serveMarginAnchor,
    volumeUnit,
    volumeL12M,
    timersAllowed: timersAllowedRef,
    oom,
    staticPnlProps,
    drillDownsOpen,
    setDrillDownsOpen,
    mouseOverPLLine,
    mouseOverPLLineFade,
    handleMouseOverPLLine,
    handleMouseLeavePLLine,
    t,
  };
}

export default usePnlConfig;
